import React, { useState } from "react";
import {
  Box,
  Container,
  Paper,
  Tabs,
  Tab,
  Typography,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import {
  Dashboard as DashboardIcon,
  AccessTime as TimeInOutIcon,
  Assignment as TimesheetIcon,
  People as TeamStatusIcon,
  Assessment as ReportsIcon,
  EditCalendar as EmployeeTimesheetIcon,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../contexts/AuthContext";
import { canManageTimeEntries } from "../../services/api";

// Import components
import TimeInOut from "./components/TimeInOut";
import MyTimesheet from "./components/MyTimesheet";
import TeamStatus from "./components/TeamStatus";
import Reports from "./components/Reports";
import EmployeeTimesheet from "./components/EmployeeTimesheet";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`timetracking-tabpanel-${index}`}
      aria-labelledby={`timetracking-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `timetracking-tab-${index}`,
    "aria-controls": `timetracking-tabpanel-${index}`,
  };
}

const TimeTrackingPage: React.FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { user } = useAuth();

  const [activeTab, setActiveTab] = useState(0);

  const canManage = canManageTimeEntries(user);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const tabs = [
    {
      label: t("timetracking.timeInOut"),
      icon: <TimeInOutIcon />,
      component: <TimeInOut />,
      show: true,
      showInMobile: true, // Show in mobile
    },
    {
      label: t("timetracking.myTimesheet"),
      icon: <TimesheetIcon />,
      component: <MyTimesheet />,
      show: true,
      showInMobile: true, // Show in mobile
    },
    {
      label: t("timetracking.teamStatusTab"),
      icon: <TeamStatusIcon />,
      component: <TeamStatus />,
      show: true, // All users can view team status per requirements
      showInMobile: true, // Show in mobile
    },
    {
      label: t("timetracking.employeeTimesheet.title"),
      icon: <EmployeeTimesheetIcon />,
      component: <EmployeeTimesheet />,
      show: canManage, // Only admins can access employee timesheet management
      showInMobile: false, // Hide in mobile (even for Admin)
    },
    {
      label: t("timetracking.reports.title"),
      icon: <ReportsIcon />,
      component: <Reports />,
      show: canManage, // Only admins can access reports
      showInMobile: false, // Hide in mobile (even for Admin)
    },
  ];

  // Filter tabs based on permissions and mobile view
  const visibleTabs = tabs.filter((tab) => {
    if (isMobile) {
      return tab.show && tab.showInMobile;
    }
    return tab.show;
  });

  return (
    <Container maxWidth="xl" sx={{ py: 1 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 3 }}>
        <Typography variant="h4">{t("timetracking.title")}</Typography>
      </Box>

      <Paper elevation={1} sx={{ width: "100%" }}>
        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            variant={isMobile ? "scrollable" : "standard"}
            scrollButtons={isMobile ? "auto" : false}
            allowScrollButtonsMobile
            sx={{
              "& .MuiTab-root": {
                minHeight: 64,
                textTransform: "none",
                fontSize: "0.95rem",
                fontWeight: 500,
                color: "text.secondary",
                "&.Mui-selected": {
                  color: "primary.main",
                  fontWeight: 600,
                },
              },
              "& .MuiTabs-indicator": {
                height: 3,
                borderRadius: "3px 3px 0 0",
              },
            }}
          >
            {visibleTabs.map((tab, index) => (
              <Tab
                key={index}
                icon={tab.icon}
                iconPosition={isMobile ? "top" : "start"}
                label={tab.label}
                {...a11yProps(index)}
                sx={{
                  minWidth: isMobile ? 120 : 160,
                  "& .MuiTab-iconWrapper": {
                    marginBottom: isMobile ? 0.5 : 0,
                    marginRight: isMobile ? 0 : 1,
                  },
                }}
              />
            ))}
          </Tabs>
        </Box>

        {visibleTabs.map((tab, index) => (
          <TabPanel key={index} value={activeTab} index={index}>
            {tab.component}
          </TabPanel>
        ))}
      </Paper>
    </Container>
  );
};

export default TimeTrackingPage;
